import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typeahead, LineLoader} from '@timmons-group/shared-react-components';
import { useLocalStorage, useStaleData } from '@timmons-group/shared-react-components/hooks';
import { createZoomOption, zoomablesOptions } from 'helpers.js';
import { PREF_STATE_KEY, ZOOMABLES_URL } from 'constants.js';
import { useAppConfig } from 'hooks/useAppConfig';

// StatePreference typeahead extender component
const StatePreference = ({ update, label, defaultLabel, initItems, prefStateKey, hideWhenSingleOption = false, ...props }) => {
  const config = useAppConfig();
  const [preferredState, setPreferredState] = useLocalStorage(prefStateKey || PREF_STATE_KEY, null);
  // If the initItems are provided then make stateData act like a local dev debug
  const [zoomables, zoomablesLoading] = useStaleData(ZOOMABLES_URL, initItems, initItems?.length > 0);

  // Notify parent of change
  useEffect(() => {
    update(preferredState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredState]);

  // Create / update the when Zoomables loading status changes
  const stateOptions = useMemo(() => {
    if (!zoomablesLoading) {
      // Map the response and sort
      const options = zoomablesOptions(zoomables, 'name');

      if (options && (options.length > 1 || !hideWhenSingleOption)) {
        // If default extent is found at it to the top
        if (config?.defaultExtent) {
          options.unshift(createZoomOption(defaultLabel, 'default', config?.defaultExtent));
        }
      }

      // Use the first item as the default unless the user has already selected a state in local storage
      let selection = options[0];
      if (preferredState) {
        //check if parsed is in the list of items...MUI autocomplete will flip a lid and spam console if you don't
        selection = options.find(item => item.value === preferredState.value) || options[0];
      }

      //update the two state hooks
      setPreferredState(selection);
      return options;
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomablesLoading]);

  /**
   * onChange handler for the Typeahead component to update the state
   * @param {*} event
   * @param {*} newValue
   */
  const statePreferenceSelected = (_, newValue) => {
    setPreferredState(newValue);
  }

  if (zoomablesLoading) {
    return (<LineLoader width={'300px'} message="Loading zoom options..." color="secondary" />);
  }

  // In cases where you don't want to show this control when only one option exists
  // we suppress skip the rendering of the control.
  if (!zoomablesLoading && stateOptions.length <= 1 && hideWhenSingleOption) {
    return null;
  }

  return <Typeahead
    sx={{}}
    renderSX={{ display: 'flex', alignItems: 'baseline' }}
    textFieldSX={{ marginLeft: 2, width: '300px'}}
    inputSX={{ paddingTop:'0px !important', paddingBottom:'0px !important' }}
    label={label}
    value={preferredState}
    disableClearable={true}
    onChange={statePreferenceSelected}
    items={stateOptions}
    {...props}
  />
}

StatePreference.propTypes = {
  hideWhenSingleOption: PropTypes.bool,
  update: PropTypes.func,
  label: PropTypes.string,
  defaultLabel: PropTypes.string,
  initItems: PropTypes.array,
  prefStateKey: PropTypes.string,
  itemsLoaded: PropTypes.func,
};

StatePreference.defaultProps = {
  defaultLabel: 'Default',
  label: 'Select a State',
};

export default StatePreference;